import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { serversAPI, clustersAPI, alertsAPI, observabilityAPI } from '../services/api';
import { useCluster } from '../context/ClusterContext';
import './Dashboard.css';

export default function Dashboard() {
  const navigate = useNavigate();
  const { selectedCluster } = useCluster();
  const [stats, setStats]         = useState({ servers: 0, nodes: 0, clusters: 0, firing: 0, resolved: 0, critical: 0, warning: 0 });
  const [trends, setTrends]       = useState(null);
  const [nodeMetrics, setNodeMetrics] = useState([]);
  const [topAlerts, setTopAlerts] = useState([]);
  const [loading, setLoading]     = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [serversRes, clustersRes, alertStatsRes, trendsRes] = await Promise.all([
        serversAPI.list().catch(() => ({ data: [] })),
        clustersAPI.list().catch(() => ({ data: [] })),
        alertsAPI.getStats().catch(() => ({ data: {} })),
        observabilityAPI.getAlertTrends(7).catch(() => ({ data: null })),
      ]);

      const aStats = alertStatsRes.data || {};
      // clustersRes already embeds each cluster's nodes[] (Rancher-managed
      // clusters especially — standalone `servers` only counts manually-added
      // hosts, which stays 0 on a Kubernetes-only deployment even though real
      // compute exists as cluster nodes).
      const totalNodes = clustersRes.data.reduce((sum, c) => sum + (c.nodes?.length || 0), 0);
      setStats({
        servers:  serversRes.data.length,
        nodes:    totalNodes,
        clusters: clustersRes.data.length,
        firing:   aStats.by_status?.firing   || 0,
        resolved: aStats.by_status?.resolved || 0,
        critical: aStats.by_severity?.critical || 0,
        warning:  aStats.by_severity?.warning  || 0,
      });

      if (trendsRes.data) {
        setTrends(trendsRes.data);
        setTopAlerts(trendsRes.data.top_alerts?.slice(0, 5) || []);
      }

      if (selectedCluster?.is_imported) {
        const nm = await clustersAPI.getNodeMetrics(selectedCluster.id).catch(() => null);
        if (nm?.data?.nodes) setNodeMetrics(nm.data.nodes.slice(0, 6));
      }
    } catch (e) {
      console.error('Dashboard load error', e);
    } finally {
      setLoading(false);
    }
  // Depend on the specific fields used (id, is_imported), not the whole object — that
  // object gets a new reference on every cluster-list refresh even when nothing about it
  // changed, which re-ran this effect and reloaded the whole dashboard every 30s via
  // AuthContext's permissions-refresh -> ClusterContext's re-select cascade.
  }, [selectedCluster?.id, selectedCluster?.is_imported]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const maxDaily = trends ? Math.max(...(trends.daily || []).map(d => d.count), 1) : 1;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <button className="button-secondary" onClick={loadAll} disabled={loading} style={{ fontSize: 13 }}>
          {loading ? '⟳' : '⟳ Refresh'}
        </button>
      </div>

      {/* Summary cards */}
      <div className="dash-cards">
        <div className="dash-card" onClick={() => navigate('/servers')}>
          <div className="dash-card-icon">🖥</div>
          <div className="dash-card-val">{stats.servers}</div>
          <div className="dash-card-label">Servers</div>
        </div>
        <div className="dash-card" onClick={() => navigate('/clusters')}>
          <div className="dash-card-icon">☸</div>
          <div className="dash-card-val">{stats.clusters}</div>
          <div className="dash-card-label">Clusters</div>
        </div>
        <div className="dash-card" onClick={() => navigate('/clusters')}>
          <div className="dash-card-icon">🧩</div>
          <div className="dash-card-val">{stats.nodes}</div>
          <div className="dash-card-label">Nodes</div>
        </div>
        <div className="dash-card dash-card-danger" onClick={() => navigate('/alerts')}>
          <div className="dash-card-icon">🔴</div>
          <div className="dash-card-val">{stats.firing}</div>
          <div className="dash-card-label">Firing Alerts</div>
        </div>
        <div className="dash-card dash-card-warn" onClick={() => navigate('/alerts')}>
          <div className="dash-card-icon">⚠</div>
          <div className="dash-card-val">{stats.critical}</div>
          <div className="dash-card-label">Critical</div>
        </div>
        <div className="dash-card dash-card-ok" onClick={() => navigate('/alerts')}>
          <div className="dash-card-icon">✓</div>
          <div className="dash-card-val">{stats.resolved}</div>
          <div className="dash-card-label">Resolved (7d)</div>
        </div>
      </div>

      <div className="dash-body">
        {/* Alert trend chart */}
        {trends && (
          <div className="dash-panel">
            <div className="dash-panel-header">
              <span>Alert Trend — Last 7 Days ({trends.total} total)</span>
              <span className="dash-panel-link" onClick={() => navigate('/alerts/search')}>Search all →</span>
            </div>
            <div className="dash-trend-chart">
              {(trends.daily || []).map((d, i) => {
                const h = Math.max(4, (d.count / maxDaily) * 80);
                return (
                  <div key={i} className="dash-bar-col" title={`${d.date}: ${d.count} alerts`}>
                    <div className="dash-bar-count">{d.count > 0 ? d.count : ''}</div>
                    <div className="dash-bar-fill" style={{ height: h }} />
                    <div className="dash-bar-date">{d.date.slice(5)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Node metrics */}
        {nodeMetrics.length > 0 && (
          <div className="dash-panel">
            <div className="dash-panel-header">
              <span>Node Resources — {selectedCluster?.name}</span>
              <span className="dash-panel-link" onClick={() => navigate('/metrics')}>Details →</span>
            </div>
            <div className="dash-nodes">
              {nodeMetrics.map(n => (
                <div key={n.name} className="dash-node-row">
                  <span className="dash-node-name">{n.name}</span>
                  <div className="dash-node-bars">
                    <div className="dash-node-bar-row">
                      <span>CPU</span>
                      <div className="dash-node-track">
                        <div className="dash-node-fill" style={{
                          width: `${n.cpu_percent}%`,
                          background: n.cpu_percent < 60 ? 'var(--color-success-500)' : n.cpu_percent < 85 ? 'var(--accent)' : 'var(--color-danger-500)'
                        }} />
                      </div>
                      <span>{n.cpu_percent}%</span>
                    </div>
                    <div className="dash-node-bar-row">
                      <span>RAM</span>
                      <div className="dash-node-track">
                        <div className="dash-node-fill" style={{
                          width: `${n.memory_percent}%`,
                          background: n.memory_percent < 70 ? 'var(--brand)' : n.memory_percent < 90 ? 'var(--accent)' : 'var(--color-danger-500)'
                        }} />
                      </div>
                      <span>{n.memory_percent}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top firing alerts */}
        {topAlerts.length > 0 && (
          <div className="dash-panel">
            <div className="dash-panel-header">
              <span>Most Frequent Alerts (7d)</span>
              <span className="dash-panel-link" onClick={() => navigate('/alerts/search')}>Search →</span>
            </div>
            <div className="dash-top-alerts">
              {topAlerts.map((a, i) => (
                <div key={i} className="dash-top-alert-row">
                  <span className="dash-rank">#{i + 1}</span>
                  <span className="dash-alert-name" title={a.name}>{a.name.length > 45 ? a.name.slice(0, 45) + '…' : a.name}</span>
                  <span className="dash-alert-count">{a.count}×</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
